import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

const Navbar = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSportsOpen, setIsSportsOpen] = useState(false);

  const navigate = useNavigate();

  function slugify(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-");
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery) {
      const searchSlug = slugify(searchQuery);
      navigate(`/search/${searchSlug}`);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSports = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSportsOpen(!isSportsOpen);
  };

  const handleSportsLink = () => {
    navigate(`/category/sports`);
    setIsSportsOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-[#0A5640] text-white shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <Link
              className="text-xl font-bold hover:text-gray-200 transition-colors"
              to="/"
            >
              The Spectrum
            </Link>

            {/* Mobile Toggler */}
            <button
              className="md:hidden p-2 rounded-md hover:bg-green-800 transition-colors focus:outline-none"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path
                    fillRule="evenodd"
                    d="M18.278 16.864a1 1 0 01-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 01-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 011.414-1.414l4.829 4.828 4.828-4.828a1 1 0 111.414 1.414l-4.828 4.829 4.828 4.828z"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M4 5h16a1 1 0 010 2H4a1 1 0 110-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2z"
                  />
                )}
              </svg>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex flex-grow items-center">
              <ul className="flex space-x-6 ml-8 font-medium">
                <li>
                  <Link
                    to="/articles"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Recent Articles
                  </Link>
                </li>
                <li className="relative group">
                  <button
                    className="flex items-center hover:text-gray-200 transition-colors"
                    onClick={handleSportsLink}
                    onMouseEnter={() => setIsSportsOpen(true)}
                    onMouseLeave={() => setIsSportsOpen(false)}
                  >
                    Sports
                    <svg
                      className="ml-1 h-4 w-4 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                    </svg>
                  </button>
                  {/* Sports Dropdown */}
                  <div
                    className={`absolute left-0 mt-0 w-48 bg-white text-gray-800 rounded-md shadow-xl py-2 z-[60] transition-all duration-200 ${isSportsOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
                    onMouseEnter={() => setIsSportsOpen(true)}
                    onMouseLeave={() => setIsSportsOpen(false)}
                  >
                    {[
                      { name: "Football", slug: "football" },
                      { name: "Men's Basketball", slug: "bball" },
                      { name: "Women's Basketball", slug: "wbball" },
                      { name: "Baseball", slug: "baseball" },
                      { name: "Softball", slug: "softball" },
                      { name: "Volleyball", slug: "vball" },
                      { name: "Golf", slug: "golf" },
                      { name: "Cross Country", slug: "cross-country" },
                      { name: "Soccer", slug: "soccer" },
                      { name: "Track and Field", slug: "tf" },
                      { name: "Wrestling", slug: "wrestling" },
                    ].map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="block px-4 py-2 hover:bg-green-50 hover:text-[#0A5640] transition-colors"
                        onClick={() => setIsSportsOpen(false)}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </li>
                <li>
                  <Link
                    to="/category/opinion"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Opinion
                  </Link>
                </li>
                <li>
                  <Link
                    to="/category/variety"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Variety
                  </Link>
                </li>
                <li>
                  <Link
                    to="/category/news"
                    className="hover:text-gray-200 transition-colors"
                  >
                    News
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Gallery
                  </Link>
                </li>
              </ul>

              <ul className="flex space-x-6 ml-auto mr-6 font-medium">
                <li>
                  <Link
                    to="/about-us"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Our Staff
                  </Link>
                </li>
                <li>
                  <Link
                    to="/apply"
                    className="hover:text-gray-200 transition-colors"
                  >
                    Apply
                  </Link>
                </li>
              </ul>

              <form className="flex items-center" onSubmit={handleSearchSubmit}>
                <input
                  className="px-3 py-1.5 rounded-l-md text-gray-800 focus:outline-none w-40 lg:w-48 text-sm"
                  type="search"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  className="bg-green-700 hover:bg-green-800 px-3 py-1.5 rounded-r-md transition-colors border-l border-green-600 text-sm font-bold"
                  type="submit"
                >
                  Go
                </button>
              </form>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`${isMenuOpen ? "max-h-[80vh] opacity-100 py-4" : "max-h-0 opacity-0 overflow-hidden"} md:hidden transition-all duration-300 ease-in-out`}
          >
            <ul className="flex flex-col space-y-3 font-medium">
              <li>
                <Link
                  to="/articles"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Recent Articles
                </Link>
              </li>
              <li>
                <button
                  className="flex items-center justify-between w-full py-1 border-b border-green-800"
                  onClick={toggleSports}
                >
                  <span>Sports</span>
                  <svg
                    className={`h-4 w-4 transition-transform ${isSportsOpen ? "rotate-180" : ""}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </button>
                <div
                  className={`${isSportsOpen ? "max-h-96 py-2" : "max-h-0 overflow-hidden"} transition-all duration-300`}
                >
                  <ul className="pl-4 grid grid-cols-2 gap-2 text-sm">
                    {[
                      { name: "Football", slug: "football" },
                      { name: "Men's Basketball", slug: "bball" },
                      { name: "Women's Basketball", slug: "wbball" },
                      { name: "Baseball", slug: "baseball" },
                      { name: "Softball", slug: "softball" },
                      { name: "Volleyball", slug: "vball" },
                      { name: "Golf", slug: "golf" },
                      { name: "Cross Country", slug: "cross-country" },
                      { name: "Soccer", slug: "soccer" },
                      { name: "Track and Field", slug: "tf" },
                      { name: "Wrestling", slug: "wrestling" },
                    ].map((cat) => (
                      <li key={cat.slug}>
                        <Link
                          to={`/category/${cat.slug}`}
                          className="block py-1 hover:text-gray-200"
                          onClick={() => {
                            setIsMenuOpen(false);
                          }}
                        >
                          {cat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
              <li>
                <Link
                  to="/category/opinion"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Opinion
                </Link>
              </li>
              <li>
                <Link
                  to="/category/variety"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Variety
                </Link>
              </li>
              <li>
                <Link
                  to="/category/news"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  News
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery_photo"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Gallery
                </Link>
              </li>
              <li>
                <Link
                  to="/about-us"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Our Staff
                </Link>
              </li>
              <li>
                <Link
                  to="/apply"
                  className="block py-1 border-b border-green-800"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Work with Us
                </Link>
              </li>
              <li>
                <form className="flex mt-2" onSubmit={handleSearchSubmit}>
                  <input
                    className="flex-grow px-3 py-2 rounded-l-md text-gray-800 focus:outline-none text-sm"
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    className="bg-green-700 hover:bg-green-800 px-4 py-2 rounded-r-md text-sm font-bold"
                    type="submit"
                  >
                    Search
                  </button>
                </form>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Main Header Section (Non-sticky) */}
      {/* <div className="bg-gray-50 border-b border-gray-100"> */}
      {/*   <div className="container mx-auto px-4 py-10"> */}
      {/*     <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8"> */}
      {/*       <header className="text-center md:text-left"> */}
      {/*         <Link to="/" className="inline-block group"> */}
      {/*           <h1 className="text-5xl md:text-7xl font-heading font-black tracking-tighter text-gray-900 group-hover:text-[#0A5640] transition-colors"> */}
      {/*             The Spectrum */}
      {/*           </h1> */}
      {/*         </Link> */}
      {/*         <p className="text-xs font-black tracking-[0.3em] mt-3 uppercase text-gray-500"> */}
      {/*           FOR THE LAND AND ITS PEOPLE • EST. 1896 */}
      {/*         </p> */}
      {/*       </header> */}
      {/*       <div className="flex justify-center md:justify-end"> */}
      {/*         <img */}
      {/*           src="/ndsu-logo-old.png" */}
      {/*           alt="NDSU Logo" */}
      {/*           className="h-28 w-auto grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500" */}
      {/*         /> */}
      {/*       </div> */}
      {/*     </div> */}
      {/*   </div> */}
      {/* </div> */}

      {/* Advertisement Bar */}
      <div className="bg-white py-6">
        <div className="container mx-auto px-4">
          <a
            href="https://deekspizza.com/"
            className="block max-w-5xl mx-auto group"
          >
            <div className="relative overflow-hidden rounded-lg shadow-sm border border-gray-100 group-hover:shadow-md transition-all">
              <img
                src="/deeks-ad.jpg"
                alt="Advertisement"
                className="w-full h-auto"
              />
              <div className="absolute top-0 right-0 bg-white/80 px-2 py-0.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest rounded-bl">
                Advertisement
              </div>
            </div>
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
